/**
 * src/VirtualShopPage.js
 *
 * VISINVOKE Virtual Shop — customer-facing website page.
 *
 * Reached via Gaming -> Online Gaming. This is the website surface that explains
 * the Virtual Shop in customer language, presents the six locked Shop Credit
 * packs, and starts a purchase through the existing VISINVOKE web/Stripe flow.
 *
 * It is NOT the Virtual Shop Admin, the Virtual Shop Tauri application, its
 * storefront implementation, or DCC. The pack pricing shown here is fetched from
 * the authoritative server endpoint (/v1/shop-credits/packs) — the client never
 * hardcodes credit quantities or USD amounts, and the purchase action lets the
 * server decide price and grant.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import API from './api';
import GuardedCheckoutConsent, { useGuardedCheckout } from './components/GuardedCheckout';
import ProductNav from './components/ProductNav';
import MoreInfoPill from './components/MoreInfoPill';
import { glassSurface } from './config/glassTheme';

const ACCENT = '#7C5CFF';
const ACCENT_DEEP = '#4C1D95';
const ACCENT_BRIGHT = '#9F7BFF';

const VS_MOTION_CSS = `
  .vs-reveal { opacity: 0; transform: translateY(26px); transition: opacity 460ms cubic-bezier(0.22,1,0.36,1), transform 460ms cubic-bezier(0.22,1,0.36,1); }
  .vs-reveal.vs-visible { opacity: 1; transform: translateY(0); }

  /* Pricing grid: 3 x 2 on desktop, 2-up on medium, 1-up on phones. */
  @media (max-width: 1040px) { .vs-price-grid { grid-template-columns: 1fr 1fr !important; } }
  @media (max-width: 680px)  { .vs-price-grid { grid-template-columns: 1fr !important; } }
  @media (max-width: 900px)  { .vs-feature-grid { grid-template-columns: 1fr !important; } }
`;

// What the Virtual Shop offers, in customer language. No backend implementation
// detail; not physical ecommerce.
const FEATURES = [
  { icon: '\u{1F3EC}', title: 'A large, growing catalogue', text: 'Explore a big and expanding catalogue of virtual products — from everyday collectibles through technology, fashion, equipment, vehicles and property.' },
  { icon: '\u{1FA99}', title: 'Acquire with Shop Credits', text: 'Every product has a Shop Credit price. Buy the Shop Credits you want, then use them to acquire the items you like.' },
  { icon: '\u{1F5C4}️', title: 'Own what you acquire', text: 'Acquired products are attached to your VISINVOKE account and stay yours. Ownership is persistent, so your collection is always there when you return.' },
  { icon: '\u{1F5C2}️', title: 'My Collection', text: 'Everything you own is gathered in My Collection, grouped into meaningful areas so a growing collection stays easy to browse.' },
  { icon: '\u{2728}', title: 'Showcase & Recently Acquired', text: 'Highlight favourites in your Showcase and see your latest additions in Recently Acquired as your collection develops.' },
  { icon: '\u{1F48E}', title: 'Premium & aspirational items', text: 'Alongside everyday items sit premium and aspirational products — the pieces worth building towards over time.' },
  { icon: '\u{1F512}', title: 'Private by default', text: 'Your collection is private by default. What you own is yours to keep to yourself.' },
  { icon: '\u{1F501}', title: 'Keep building over time', text: 'Browse whenever you like and keep adding to your collection. Items you own stay owned even as the catalogue changes.' },
];

function PackCard({ pack, busy, onBuy }) {
  const [hover, setHover] = useState(false);
  const best = pack.bestValue;
  return (
    <div
      className="vs-reveal vs-visible"
      style={{
        ...card.base,
        ...(best ? card.best : {}),
        transform: hover ? 'translateY(-4px)' : 'translateY(0)',
        boxShadow: hover
          ? `0 22px 54px rgba(0,0,0,0.5), 0 0 0 1px ${ACCENT}66`
          : (best ? `0 0 0 1px ${ACCENT}44, 0 18px 50px rgba(124,92,255,0.16), 0 14px 44px rgba(0,0,0,0.4)` : '0 14px 40px rgba(0,0,0,0.35)'),
      }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      {best && <div style={card.bestTopLine} />}
      <div style={card.badgeRow}>
        {pack.badge
          ? <span style={best ? card.badgeBest : card.badge}>{pack.badge}</span>
          : <span style={card.badgeBase}>BASE VALUE</span>}
      </div>

      <div style={card.name}>{pack.name}</div>

      {/* Shop Credits — the dominant figure */}
      <div style={card.creditsRow}>
        <span style={card.creditsNum}>{Number(pack.credits).toLocaleString()}</span>
      </div>
      <div style={card.creditsLabel}>SHOP CREDITS</div>

      {/* USD price — equally prominent */}
      <div style={card.priceRow}>
        <span style={card.priceNum}>{pack.priceDisplay}</span>
      </div>

      <p style={card.blurb}>{pack.blurb}</p>

      <button
        type="button"
        disabled={!!busy}
        style={{ ...card.cta, ...(busy ? card.ctaBusy : {}) }}
        onClick={() => onBuy(pack.key)}
        onMouseEnter={e => { if (!busy) { e.currentTarget.style.filter = 'brightness(1.12)'; e.currentTarget.style.transform = 'translateY(-2px)'; } }}
        onMouseLeave={e => { e.currentTarget.style.filter = 'none'; e.currentTarget.style.transform = 'translateY(0)'; }}
      >
        {busy ? 'Starting checkout…' : pack.ctaLabel}
      </button>
    </div>
  );
}

export default function VirtualShopPage() {
  // Shared consent controller. Inert unless the SERVER refuses the checkout pending an
  // immediate-supply acknowledgement; then it renders the server's own wording and retries.
  const consentCheckout = useGuardedCheckout();
  const navigate = useNavigate();
  const [packs, setPacks] = useState(null);   // null = loading
  const [loadError, setLoadError] = useState('');
  const [busyKey, setBusyKey] = useState('');
  const [buyError, setBuyError] = useState('');

  useEffect(() => {
    let alive = true;
    API.get('/v1/shop-credits/packs')
      .then(res => { if (alive) setPacks(res.data && Array.isArray(res.data.packs) ? res.data.packs : []); })
      .catch(() => { if (alive) { setPacks([]); setLoadError('Shop Credit pricing is temporarily unavailable. Please try again shortly.'); } });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    let style = document.getElementById('vs-motion-css');
    if (!style) { style = document.createElement('style'); style.id = 'vs-motion-css'; document.head.appendChild(style); }
    style.textContent = VS_MOTION_CSS;
  }, []);

  async function handleBuy(packKey) {
    setBuyError('');
    setBusyKey(packKey);
    try {
      const res = await consentCheckout.start(() => API.post('/v1/shop-credits/checkout', { pack_key: packKey }), { purchaseType: 'shop_credit_pack', intendedSelector: packKey });
      if (res.data && res.data.checkout_url) { window.location.href = res.data.checkout_url; return; }
      setBuyError('Could not start checkout. Please try again.');
      setBusyKey('');
    } catch (e) {
      setBuyError(e.response?.data?.error || 'Could not start checkout. Please try again.');
      setBusyKey('');
    }
  }

  return (
    <div style={pg.page}>
      <GuardedCheckoutConsent checkout={consentCheckout} />
      <ProductNav />

      {/* ---------------------------------------------------------- HERO */}
      <section style={pg.headerSection}>
        <div style={pg.container}>
          <button type="button" style={pg.back} onClick={() => navigate('/dashboard/gaming')}>{'←'} Gaming</button>
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 6 }}>
            <h1 style={pg.headerTitle}>VISINVOKE Virtual Shop</h1>
          </div>
          <p style={pg.headerSub}>
            A visual marketplace of virtual products. Explore a large and growing catalogue, acquire the items you like with Shop Credits, own them permanently, and build a collection that is uniquely yours.
          </p>
        </div>
      </section>

      {/* ------------------------------------------------------ FEATURES */}
      <section style={pg.featuresSection}>
        <div style={pg.container}>
          <div className="vs-feature-grid" style={pg.featureGrid}>
            {FEATURES.map((f, i) => (
              <div key={i} style={pg.featureCard}>
                <div style={pg.featureIcon}>{f.icon}</div>
                <div style={pg.featureTitle}>{f.title}</div>
                <div style={pg.featureText}>{f.text}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ------------------------------------------------------- PRICING */}
      <section style={pg.pricingSection}>
        <div style={pg.container}>
          <div style={pg.pricingHead}>
            <h2 style={pg.pricingHeading}>BUILD YOUR COLLECTION</h2>
            <p style={pg.pricingSub}>
              Choose the Shop Credits you want and spend them whenever you like. No subscription. Your credits stay with your VISINVOKE account until you use them.
            </p>
          </div>

          {buyError && <div style={pg.buyError}>{buyError}</div>}

          {packs === null ? (
            <div style={pg.pricingNote}>Loading Shop Credit packs…</div>
          ) : packs.length === 0 ? (
            <div style={pg.pricingNote}>{loadError || 'Shop Credit packs are unavailable right now.'}</div>
          ) : (
            <div className="vs-price-grid" style={pg.priceGrid}>
              {packs.map(p => (
                <PackCard key={p.key} pack={p} busy={busyKey === p.key} onBuy={handleBuy} />
              ))}
            </div>
          )}

          <p style={pg.pricingFoot}>
            Shop Credits are used only inside Virtual Shop. Product credit prices range from everyday collectibles through to premium vehicles, property and aspirational items.
          </p>

          <div style={pg.moreInfoWrap}>
            <MoreInfoPill productKey="virtual-shop" accent={ACCENT} />
          </div>
        </div>
      </section>
    </div>
  );
}

const pg = {
  page: {
    minHeight: '100vh',
    background: 'radial-gradient(1200px 640px at 50% -4%, rgba(124,92,255,0.10) 0%, rgba(0,0,0,0) 46%), radial-gradient(1000px 720px at 100% 112%, rgba(124,92,255,0.05) 0%, rgba(0,0,0,0) 55%), #080B11',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    color: '#C7CFDC',
  },
  container: { maxWidth: 1280, margin: '0 auto', padding: '0 40px', boxSizing: 'border-box' },

  headerSection: { padding: '32px 0 8px' },
  back: {
    ...glassSurface(ACCENT_DEEP, ACCENT, ACCENT_BRIGHT),
    borderRadius: 8, padding: '8px 14px', fontSize: 13, fontWeight: 600,
    cursor: 'pointer',
  },
  headerTitle: { fontSize: 50, fontWeight: 900, color: '#ffffff', lineHeight: 1.1, margin: 0, letterSpacing: '-1px', textAlign: 'center' },
  headerSub: { fontSize: 18, fontWeight: 500, color: '#C7CFDC', lineHeight: 1.6, margin: '14px auto 0', textAlign: 'center', maxWidth: 820 },

  featuresSection: { padding: '30px 0 8px' },
  featureGrid: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 18 },
  featureCard: {
    background: '#0F1520', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 16,
    padding: '22px 20px', minWidth: 0,
  },
  featureIcon: { fontSize: 26, marginBottom: 12 },
  featureTitle: { fontSize: 16, fontWeight: 800, color: '#ffffff', marginBottom: 8, lineHeight: 1.25, minHeight: 40 },
  featureText: { fontSize: 13.5, color: '#9FAABC', lineHeight: 1.6 },

  pricingSection: { padding: '48px 0 96px' },
  pricingHead: { textAlign: 'center', marginBottom: 30 },
  pricingHeading: { fontSize: 30, fontWeight: 900, color: '#ffffff', letterSpacing: '0.12em', margin: 0, lineHeight: 1.2 },
  pricingSub: { fontSize: 16, fontWeight: 500, color: '#C7CFDC', lineHeight: 1.6, margin: '12px auto 0', maxWidth: 720 },

  buyError: {
    maxWidth: 720, margin: '0 auto 18px', textAlign: 'center', color: '#fca5a5',
    background: 'rgba(239,68,68,0.10)', border: '1px solid rgba(239,68,68,0.35)',
    borderRadius: 10, padding: '10px 16px', fontSize: 14, fontWeight: 600,
  },
  pricingNote: { textAlign: 'center', color: '#9FAABC', fontSize: 15, padding: '40px 0' },

  priceGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 22, alignItems: 'stretch' },

  pricingFoot: { textAlign: 'center', color: '#8F9AAC', fontSize: 14, lineHeight: 1.6, margin: '30px auto 0', maxWidth: 760 },
  moreInfoWrap: { maxWidth: 420, margin: '26px auto 0' },
};

const card = {
  base: {
    position: 'relative',
    overflow: 'hidden',
    minWidth: 0,
    background: '#0F1520',
    border: '1px solid rgba(255,255,255,0.09)',
    borderRadius: 18,
    padding: '30px 26px 28px',
    display: 'flex',
    flexDirection: 'column',
    transition: 'transform 200ms cubic-bezier(0.22,1,0.36,1), box-shadow 220ms ease',
  },
  best: { border: '1px solid rgba(124,92,255,0.34)' },
  bestTopLine: {
    position: 'absolute', top: 0, left: 0, right: 0, height: 3,
    background: `linear-gradient(90deg, ${ACCENT_DEEP}, ${ACCENT_BRIGHT})`, pointerEvents: 'none',
  },
  badgeRow: { minHeight: 24, marginBottom: 16 },
  badge: {
    ...glassSurface(ACCENT_DEEP, ACCENT, ACCENT_BRIGHT),
    display: 'inline-block',
    fontSize: 11, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase',
    borderRadius: 100, padding: '5px 12px',
  },
  badgeBest: {
    ...glassSurface(ACCENT_DEEP, ACCENT, ACCENT_BRIGHT),
    display: 'inline-block',
    fontSize: 11, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase',
    borderRadius: 100, padding: '5px 12px',
  },
  badgeBase: {
    ...glassSurface('#334155', '#64748B', '#94A3B8'),
    display: 'inline-block',
    fontSize: 11, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase',
    borderRadius: 100, padding: '5px 12px',
  },
  name: { fontSize: 13, fontWeight: 800, letterSpacing: '0.16em', textTransform: 'uppercase', color: '#8F9AAC', marginBottom: 14 },
  creditsRow: { display: 'flex', alignItems: 'baseline', gap: 6, lineHeight: 1 },
  creditsNum: { fontFamily: 'Georgia, "Times New Roman", serif', fontSize: 42, fontWeight: 900, color: '#ffffff', letterSpacing: '-1px', lineHeight: 1 },
  creditsLabel: { fontSize: 12, fontWeight: 700, letterSpacing: '0.14em', color: ACCENT_BRIGHT, marginTop: 6, marginBottom: 16 },
  priceRow: { display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 16 },
  priceNum: { fontSize: 30, fontWeight: 900, color: '#ffffff', letterSpacing: '-0.5px', lineHeight: 1 },
  blurb: { fontSize: 13.5, color: '#9FAABC', lineHeight: 1.6, margin: '0 0 22px' },
  cta: {
    ...glassSurface(ACCENT_DEEP, ACCENT, ACCENT_BRIGHT),
    marginTop: 'auto', width: '100%', borderRadius: 12, padding: '14px 18px',
    fontSize: 13.5, fontWeight: 800, letterSpacing: '0.04em', cursor: 'pointer',
    transition: 'filter 180ms ease, transform 160ms cubic-bezier(0.22,1,0.36,1)',
  },
  ctaBusy: { opacity: 0.7, cursor: 'default' },
};
